/**
 * Floating Legend Component for 4D visualization modes
 */
import { Button } from '@/components/ui/button';
import { X } from 'lucide-react';

export function FloatingLegend({ mode, kpiColorSet, visible, onToggle }) {
  // Same statuses the macro schedule reports, so the viewer and the grid agree.
  const scheduleColors = [
    { name: 'Completed – On Time', hex: '#10B981' },
    { name: 'Completed – Late', hex: '#F59E0B' },
    { name: 'On Track', hex: '#3B82F6' },
    { name: 'At Risk', hex: '#EAB308' },
    { name: 'Delayed', hex: '#FB923C' },
    { name: 'Overdue', hex: '#EF4444' },
    { name: 'Late Start', hex: '#F97316' },
    { name: 'Forecast Delay', hex: '#FBBF24' },
    { name: 'Not Started – Overdue', hex: '#DC2626' },
    { name: 'Not Started', hex: '#94A3B8' },
    { name: 'Ghost/Unmapped', hex: '#E0E0E0' },
  ];
  const planColors = [
    { name: 'Planned Active', hex: '#3B82F6' },
    { name: 'Planned Complete', hex: '#3B82F6' },
    { name: 'Ghost', hex: '#E0E0E0' },
  ];
  const actualColors = [
    { name: 'Actual Active', hex: '#10B981' },
    { name: 'Actual Complete', hex: '#10B981' },
    { name: 'Ghost', hex: '#E0E0E0' },
  ];
  // CRITICAL_PATH: opacity-encoded lifecycle states so the user knows what each
  // shade of red means without having to hover every object.
  const criticalColors = [
    { name: 'Done — CP complete', hex: '#10B981', alpha: 1.0 },
    { name: 'Live — CP in progress', hex: '#DC2626', alpha: 1.0 },
    { name: 'Overdue — CP slipped', hex: '#DC2626', alpha: 1.0, ring: true },
    { name: 'Current window', hex: '#DC2626', alpha: 0.75 },
    { name: 'Upcoming CP', hex: '#DC2626', alpha: 0.35 },
    { name: 'Ghost — Non-CP', hex: '#E0E0E0', alpha: 0.4 },
  ];

  let colors = scheduleColors;
  if (mode === 'PLAN') colors = planColors;
  else if (mode === 'ACTUAL') colors = actualColors;
  else if (mode === 'CRITICAL_PATH') colors = criticalColors;
  else if (mode === 'KPI' && kpiColorSet?.colors) colors = kpiColorSet.colors.map(c => ({ name: c.colorName, hex: c.colorHex }));

  if (!visible) return null;

  return (
    <div className="absolute bottom-2 left-2 bg-background/90 backdrop-blur-md border rounded-lg p-2 z-20 max-w-xs" data-testid="floating-legend">
      <div className="flex items-center justify-between mb-1.5">
        <span className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
          Legend {mode === 'CRITICAL_PATH' ? '· Critical Path' : ''}
        </span>
        <Button variant="ghost" size="icon" className="h-5 w-5" onClick={onToggle}><X className="w-3 h-3" /></Button>
      </div>
      <div className={`grid ${mode === 'CRITICAL_PATH' ? 'grid-cols-1' : 'grid-cols-2'} gap-x-3 gap-y-0.5`}>
        {colors.map((c, i) => (
          <div key={i} className="flex items-center gap-1.5 text-[10px]">
            <div
              className={`w-2.5 h-2.5 rounded-full shrink-0 ${c.ring ? 'ring-2 ring-red-500 ring-offset-1 ring-offset-background' : ''}`}
              style={{ backgroundColor: c.hex, opacity: c.alpha != null ? c.alpha : (c.hex === '#E0E0E0' ? 0.3 : 1) }}
            />
            <span className="truncate">{c.name}</span>
          </div>
        ))}
      </div>
    </div>
  );
}
